import React, { useState, useCallback } from 'react';
import { SpotGenre } from '../types/core';
import { announceToScreenReader, generateId } from '../utils/accessibility';
import { Button } from './Button';

export interface ConditionSelectorProps {
  onConditionsChange: (conditions: PlanConditions) => void;
  disabled?: boolean;
  className?: string;
}

export interface PlanConditions {
  duration: number; // 30, 60, 90, 120, 180
  genres: SpotGenre[];
  people: number; // 1, 2, 3-4 (represented as 1, 2, 4)
  rainBias: boolean;
}

export interface ValidationErrors {
  duration?: string;
  genres?: string;
  people?: string;
}

// Duration options in minutes
const DURATION_OPTIONS = [
  { value: 30, label: '30分' },
  { value: 60, label: '1時間' },
  { value: 90, label: '1時間30分' },
  { value: 120, label: '2時間' },
  { value: 180, label: '3時間' }
];

// Genre options with Japanese labels
const GENRE_OPTIONS: Array<{ value: SpotGenre; label: string; description: string }> = [
  { value: 'cafe', label: 'カフェ', description: 'コーヒーショップ・喫茶店' },
  { value: 'bookstore', label: '書店', description: '本屋・図書館' },
  { value: 'park', label: '公園', description: '公園・緑地' },
  { value: 'museum', label: '美術館', description: '美術館・博物館' },
  { value: 'sweets', label: 'スイーツ', description: 'ケーキ店・和菓子店' },
  { value: 'event', label: 'イベント', description: '勉強会・セミナー' },
  { value: 'bar', label: 'バー', description: 'バー・居酒屋' },
  { value: 'restaurant', label: 'レストラン', description: 'レストラン・食事処' }
];

// People options
const PEOPLE_OPTIONS = [
  { value: 1, label: '1人', description: '一人での外出' },
  { value: 2, label: '2人', description: '二人での外出' },
  { value: 4, label: '3-4人', description: '3〜4人のグループ' }
];

export function ConditionSelector({
  onConditionsChange,
  disabled = false,
  className = ''
}: ConditionSelectorProps) {
  // Form state
  const [duration, setDuration] = useState<number>(60); // Default to 1 hour
  const [selectedGenres, setSelectedGenres] = useState<SpotGenre[]>(['cafe']); // Default to cafe
  const [people, setPeople] = useState<number>(1); // Default to 1 person
  const [rainBias, setRainBias] = useState<boolean>(false);
  
  // Validation state
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  // Generate unique IDs for accessibility
  const durationFieldsetId = generateId('duration-fieldset');
  const genresFieldsetId = generateId('genres-fieldset');
  const peopleFieldsetId = generateId('people-fieldset');
  const rainBiasId = generateId('rain-bias');
  const errorsId = generateId('condition-errors');

  // Validate form
  const validateForm = useCallback((): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    if (!DURATION_OPTIONS.some(option => option.value === duration)) {
      newErrors.duration = '有効な時間を選択してください';
    }

    if (selectedGenres.length === 0) {
      newErrors.genres = '少なくとも1つのジャンルを選択してください';
    } else if (selectedGenres.length > 4) {
      newErrors.genres = '選択できるジャンルは最大4つまでです';
    }

    if (!PEOPLE_OPTIONS.some(option => option.value === people)) {
      newErrors.people = '有効な人数を選択してください';
    }

    return newErrors;
  }, [duration, selectedGenres, people]);

  // Handle duration change
  const handleDurationChange = useCallback((newDuration: number) => {
    setDuration(newDuration);
    
    if (hasSubmitted) {
      const newErrors = validateForm();
      setErrors(newErrors);
    }
    
    announceToScreenReader(`時間を${DURATION_OPTIONS.find(opt => opt.value === newDuration)?.label}に変更しました`, 'polite');
  }, [hasSubmitted, validateForm]);

  // Handle genre toggle
  const handleGenreToggle = useCallback((genre: SpotGenre) => {
    const newGenres = selectedGenres.includes(genre)
      ? selectedGenres.filter(g => g !== genre)
      : [...selectedGenres, genre];
    
    setSelectedGenres(newGenres);
    
    if (hasSubmitted) {
      const newErrors = validateForm();
      setErrors(newErrors);
    }

    const genreLabel = GENRE_OPTIONS.find(opt => opt.value === genre)?.label;
    const action = selectedGenres.includes(genre) ? '選択解除' : '選択';
    announceToScreenReader(`${genreLabel}を${action}しました`, 'polite');
  }, [selectedGenres, hasSubmitted, validateForm]);

  // Handle people change
  const handlePeopleChange = useCallback((newPeople: number) => {
    setPeople(newPeople);
    
    if (hasSubmitted) {
      const newErrors = validateForm();
      setErrors(newErrors);
    }
    
    announceToScreenReader(`人数を${PEOPLE_OPTIONS.find(opt => opt.value === newPeople)?.label}に変更しました`, 'polite');
  }, [hasSubmitted, validateForm]);

  // Handle rain bias toggle
  const handleRainBiasToggle = useCallback(() => {
    const newRainBias = !rainBias;
    setRainBias(newRainBias);
    
    announceToScreenReader(
      newRainBias ? '雨寄せオプションを有効にしました' : '雨寄せオプションを無効にしました',
      'polite'
    );
  }, [rainBias]);

  // Handle form submission
  const handleSubmit = useCallback(() => {
    setHasSubmitted(true);
    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const conditions: PlanConditions = {
        duration,
        genres: selectedGenres,
        people,
        rainBias
      };
      
      onConditionsChange(conditions);
      announceToScreenReader('条件を設定しました', 'polite');
    } else {
      const errorCount = Object.keys(validationErrors).length;
      announceToScreenReader(`入力エラーが${errorCount}件あります。修正してください。`, 'assertive');
    }
  }, [duration, selectedGenres, people, rainBias, onConditionsChange, validateForm]);

  // Check if form has errors
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Duration Selection */}
      <fieldset>
        <legend className="text-base font-medium text-gray-900 mb-3">
          外出時間
        </legend>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {DURATION_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleDurationChange(option.value)}
              disabled={disabled}
              className={`
                px-3 py-2 text-sm font-medium rounded-md border transition-all duration-200
                min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${duration === option.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              aria-pressed={duration === option.value}
              role="radio"
              aria-describedby={errors.duration ? errorsId : undefined}
            >
              {option.label}
            </button>
          ))}
        </div>
        {errors.duration && (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errors.duration}
          </p>
        )}
      </fieldset>

      {/* Genre Selection */}
      <fieldset>
        <legend className="text-base font-medium text-gray-900 mb-3">
          ジャンル（複数選択可）
        </legend>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {GENRE_OPTIONS.map((option) => {
            const isSelected = selectedGenres.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleGenreToggle(option.value)}
                disabled={disabled}
                className={`
                  p-3 text-left rounded-md border transition-all duration-200
                  min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  ${isSelected
                    ? 'bg-blue-50 text-blue-900 border-blue-300'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                aria-pressed={isSelected}
                role="checkbox"
                aria-checked={isSelected}
                aria-describedby={errors.genres ? errorsId : undefined}
              >
                <div className="font-medium text-sm">
                  {option.label}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {option.description}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-2 text-sm text-gray-500">
          選択中: {selectedGenres.length}件 / 最大4件
        </div>
        {errors.genres && (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errors.genres}
          </p>
        )}
      </fieldset>

      {/* People Selection */}
      <fieldset>
        <legend className="text-base font-medium text-gray-900 mb-3">
          人数
        </legend>
        <div className="grid grid-cols-3 gap-3">
          {PEOPLE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handlePeopleChange(option.value)}
              disabled={disabled}
              className={`
                p-3 text-center rounded-md border transition-all duration-200
                min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${people === option.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              aria-pressed={people === option.value}
              role="radio"
              aria-describedby={errors.people ? errorsId : undefined}
            >
              <div className="font-medium text-sm">
                {option.label}
              </div>
              <div className="text-xs mt-1 opacity-75">
                {option.description}
              </div>
            </button>
          ))}
        </div>
        {errors.people && (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errors.people}
          </p>
        )}
      </fieldset>

      {/* Rain Bias Toggle */}
      <div className="flex items-start space-x-3">
        <button
          type="button"
          onClick={handleRainBiasToggle}
          disabled={disabled}
          className={`
            relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
            transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            ${rainBias ? 'bg-blue-600' : 'bg-gray-200'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          role="switch"
          aria-checked={rainBias}
          aria-labelledby={rainBiasId}
        >
          <span
            className={`
              pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
              transition duration-200 ease-in-out
              ${rainBias ? 'translate-x-5' : 'translate-x-0'}
            `}
          />
        </button>
        <div className="flex-1">
          <label id={rainBiasId} className="text-sm font-medium text-gray-900 cursor-pointer">
            雨寄せオプション
          </label>
          <p className="text-sm text-gray-500">
            雨の可能性がある場合、屋内スポットを優先して提案します
          </p>
        </div>
      </div>

      {/* Error Summary */}
      {hasErrors && hasSubmitted && (
        <div 
          id={errorsId}
          className="p-3 bg-red-50 border border-red-200 rounded-md"
          role="alert"
          aria-live="assertive"
        >
          <h3 className="text-sm font-medium text-red-800 mb-2">
            入力エラーがあります
          </h3>
          <ul className="text-sm text-red-600 space-y-1">
            {Object.entries(errors).map(([field, message]) => (
              <li key={field}>• {message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Submit Button */}
      <div className="pt-4">
        <Button
          onClick={handleSubmit}
          disabled={disabled}
          variant="primary"
          className="w-full"
        >
          この条件でプランを検索
        </Button>
      </div>
    </div>
  );
}